import React, { useState, useEffect, useContext } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import MenuBar from './editor/MenuBar'
import classNames from 'classnames'
import { FormattedMessage } from '~/translations/utils'
import { REPORTEDBY_SMS } from '../contants'
import Delegate from './editor/Delegate'
import Attachments from './editor/Attachments'
import { post } from '~/utils/api'
import SelectCC from './editor/SelectCC'
import { TicketContext } from '~/components/tickets/Show'
import store from 'store2'
import useResizeObserver from 'use-resize-observer'
import Dropdown from '~/components/common/Dropdown'

// define your extension array
const extensions = [StarterKit]
const content = ''

const setStorage = (id: number, form: any) =>
  store.set('ticket_answer_' + id, JSON.stringify(form))

const removeStorage = (id: number) => store.remove('ticket_answer_' + id)

const getStorage = (id: number) => {
  const item = store.get('ticket_answer_' + id)

  return item ? JSON.parse(item) : null
}

type Props = {
  type: 'public' | 'private'
  setType: (type: 'public' | 'private') => void
}

const Editor: React.FC<Props> = ({ type, setType }) => {
  const {
    ticket,
    primaryEmail,
    responseTemplates,
    miniEel,
    answers,
    contacts,
  } = useContext(TicketContext)

  const tiptap = useEditor({
    extensions,
    content,
    onUpdate: ({ editor }) => {
      setStorage(ticket.id, editor.getHTML())
    },
  })

  const { client, reportedBy, emailFrom } = ticket

  const [error, setError] = useState('')
  const [disabled, setDisabled] = useState(false)
  const [attachments, setAttachments] = useState<number[]>([])
  const [showDelegate, setShowDelegate] = useState(false)
  const [delegateTo, setDelegateTo] = useState<string[]>([])
  const [activeTemplate, setActiveTemplate] = useState(false)
  const [cc, setCc] = useState<string[]>([])
  const [disablePublic, setDisablePublic] = useState(false)
  const [octopusOff, setOctopusOff] = useState(false)
  const readOnly = ticket.category && ticket.category.readOnly

  const { ref, width = 0 } = useResizeObserver<HTMLElement>()

  const lastAnswer = answers.filter(
    answer => answer.private == false && answer.newState == null
  )

  const answerFromSMS = ticket.answers.find(answer => answer.sms)
  const number = answerFromSMS ? answerFromSMS.sms?.clientPhone : null

  const add = async (tab: string, state: number) => {
    setDisabled(true)
    setError('')

    if (tiptap?.getText()) {
      let res: any = await post(`tickets/${ticket.id}/addTicketAnswer`, {
        tab: tab,
        state: state,
        content: tiptap?.getHTML(),
        delegate: showDelegate,
        delegateTo: delegateTo,
        attachments: attachments,
        cc: cc,
      })

      if (res.content) {
        setError(
          intl.formatMessage({
            id: 'ticket / answers / validate / error / empty content',
          })
        )
        setDisabled(false)
      } else {
        setShowDelegate(false)
        setDisabled(false)
        setAttachments([])
        tiptap?.commands.clearContent()
        tiptap?.commands.focus()
        removeStorage(ticket.id)
      }
    } else {
      setError(
        intl.formatMessage({
          id: 'ticket / answers / validate / error / empty content',
        })
      )
      setDisabled(false)
    }
  }

  useEffect(() => {
    if (
      ((!ticket.emailFrom && !miniEel) || !primaryEmail) &&
      ticket.reportedBy != 5
    ) {
      setType('private')
      setDisablePublic(true)
    }

    if (!ticket.emailFrom && primaryEmail && miniEel) {
      setOctopusOff(true)
    }
  }, [])

  useEffect(() => {
    const content = getStorage(ticket.id)
    content && tiptap?.commands.setContent(content)
  }, [tiptap])

  const addResponseTemplate = (e, template) => {
    e.preventDefault()
    tiptap?.commands.insertContent(template)
    tiptap?.commands.focus()
    setActiveTemplate(false)
  }

  return (
    <>
      {!octopusOff && !readOnly && (
        <>
          {ticket.client && !disablePublic && (
            <div className="tabs is-boxed">
              <ul>
                <li
                  className={classNames('', {
                    'is-active': type == 'public',
                  })}
                  onClick={() => setType('public')}
                >
                  <a>
                    <span className="icon is-small">
                      <span className="fa-solid fa-paper-plane-top"></span>
                    </span>
                    <span>
                      <FormattedMessage id="ticket / editor / tab / public" />
                    </span>
                  </a>
                </li>
                <li
                  className={classNames('is-note', {
                    'is-active': type == 'private',
                  })}
                  onClick={() => setType('private')}
                >
                  <a>
                    <span className="icon is-small">
                      <span className="fa-solid fa-note"></span>
                    </span>
                    <span>
                      <FormattedMessage id="ticket / editor / tab / private" />
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          )}
          <div
            className={classNames('editor', {
              'editor--note': type == 'private' && client,
            })}
            ref={ref}
          >
            {type == 'public' && reportedBy == REPORTEDBY_SMS && number && (
              <div className="ticket-show-editor__from">
                <span className="ticket-show-editor__from-label">do:</span>
                <span className="ticket-show-editor__from-data">
                  <span>{number}</span>
                </span>
              </div>
            )}
            {type == 'public' && primaryEmail && emailFrom && (
              <>
                <div className="ticket-show-editor__from">
                  <span className="ticket-show-editor__from-label">do:</span>
                  <span className="ticket-show-editor__from-data">
                    <span>{primaryEmail}</span>
                  </span>
                </div>
                <div className="ticket-show-editor__cc">
                  <SelectCC
                    cc={cc}
                    setCc={setCc}
                    lastAnswer={lastAnswer.length ? lastAnswer[0] : null}
                    contacts={contacts}
                  />
                </div>
              </>
            )}
            {tiptap && <MenuBar editor={tiptap} />}
            <EditorContent
              className="editor__content"
              editor={tiptap}
              onClick={() => tiptap?.commands.focus()}
            />
          </div>
          <div
            className={classNames('ticket-show-editor__bottom', {
              'ticket-show-editor__bottom--note': type == 'private' && client,
              'ticket-show-editor__bottom--small': width < 450,
            })}
          >
            <div>
              <div className="ticket-show-editor__buttons">
                <Attachments
                  ticketId={ticket.id}
                  primaryEmail={primaryEmail}
                  type={type}
                  attachments={attachments}
                  setAttachments={setAttachments}
                />
                <button
                  onClick={() => setShowDelegate(!showDelegate)}
                  type="button"
                  className={classNames('button is-default', {
                    'is-info': showDelegate,
                  })}
                >
                  <span>
                    {intl.formatMessage({ id: 'ticket / editor / delegate' })}
                  </span>
                  <span className="icon is-small">
                    <span className="fas fa-share ticket-editor__icon"></span>
                  </span>
                </button>
                {responseTemplates.length > 0 && (
                  <Dropdown
                    right={true}
                    trigger={
                      <button
                        className="button"
                        aria-haspopup="true"
                        aria-controls="dropdown-menu3"
                      >
                        <span>
                          <FormattedMessage id="ticket / editor / response template" />
                        </span>
                        <span className="icon is-small">
                          <i
                            className="fas fa-angle-down"
                            aria-hidden="true"
                          ></i>
                        </span>
                      </button>
                    }
                  >
                    {responseTemplates.map((template, index) => (
                      <a
                        href="#"
                        key={index}
                        className="dropdown-item"
                        onClick={e => addResponseTemplate(e, template.content)}
                      >
                        {template.name}
                      </a>
                    ))}
                  </Dropdown>
                )}
              </div>
              {showDelegate && (
                <Delegate ticket={ticket} setDelegateTo={setDelegateTo} />
              )}
            </div>
            {ticket.client ? (
              <button
                onClick={() => add(type, 0)}
                type="submit"
                className={classNames('button is-info', {
                  'is-loading': disabled,
                })}
                disabled={disabled}
              >
                {type == 'private' ? (
                  <FormattedMessage id="ticket / editor / add / private" />
                ) : reportedBy == REPORTEDBY_SMS ? (
                  <FormattedMessage id="ticket / editor / add / sms" />
                ) : (
                  <FormattedMessage id="ticket / editor / add / email" />
                )}
              </button>
            ) : (
              <button
                type="submit"
                disabled={disabled}
                className={classNames('button is-info', {
                  'is-loading': disabled,
                })}
                onClick={() => add('private', 0)}
              >
                {intl.formatMessage({ id: 'ticket / editor / add / task' })}
              </button>
            )}
          </div>
          {error && <p className="help is-danger">{error}</p>}
        </>
      )}
    </>
  )
}

export default Editor
